import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchEpicDiagnostics } from '../../lib/api/epics';
import type { DiagEventView } from '../../lib/api/epics';
import { StatusBadge } from '../ui/StatusBadge';
import type { StatusTone } from '../ui/StatusBadge';

/**
 * Diagnostics — surfaced swallowed errors ("nothing silently hidden"). The
 * orchestrator/webhook write a DIAG# row whenever they catch a non-fatal error
 * that previously only reached CloudWatch; this panel shows them per-epic so a
 * real failure can't hide in AWS logs. Null-discipline: no DIAG# rows → nothing
 * rendered (a clean epic shows no panel).
 */

const LEVEL_TONE: Record<string, StatusTone> = { error: 'danger', warn: 'warning', info: 'neutral' };
const LEVEL_ORDER: DiagEventView['level'][] = ['error', 'warn', 'info'];

function relTime(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const m = Math.round(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  return h < 24 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
}

export const DiagnosticsPanel: React.FC<{ epicId: string }> = ({ epicId }) => {
  const query = useQuery({
    queryKey: ['epic-diagnostics', epicId],
    queryFn: () => fetchEpicDiagnostics(epicId),
    refetchInterval: 30_000,
    retry: false,
  });

  const data = query.data;
  if (!data || data.summary.total === 0) return null;

  const { events, summary } = data;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">🩺 Diagnostics</h3>
        <span className="text-xs text-gray-500">
          {LEVEL_ORDER.filter((l) => (summary.by_level[l] ?? 0) > 0)
            .map((l) => `${summary.by_level[l]} ${l}`).join(' · ') || `${summary.total}`}
        </span>
      </div>
      <p className="mb-3 text-[11px] text-gray-400">
        Non-fatal errors Forge caught while working this epic. Previously visible only in CloudWatch — surfaced here so nothing is silently hidden.
      </p>
      <div className="divide-y divide-gray-100">
        {events.map((e) => (
          <div key={e.id} className="flex items-start justify-between gap-2 py-1.5">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <StatusBadge tone={LEVEL_TONE[e.level] ?? 'neutral'}>{e.level}</StatusBadge>
                <code className="truncate font-mono text-[11px] text-gray-500">{e.context}</code>
                {e.story_id != null && <span className="text-[11px] text-gray-400">story {e.story_id}</span>}
              </div>
              <div className="mt-0.5 whitespace-pre-wrap break-words text-xs text-gray-700">{e.message}</div>
            </div>
            <span className="shrink-0 text-[11px] text-gray-400">{relTime(e.created_at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
